import OnboardingAppBar from "@features/auth/components/OnboardingAppBar";
import { useAppStore } from "@hooks/useAppStore";
import EmptyState from "@memorang/ui/components/EmptyView";
import Loader from "@memorang/ui/components/Loader";
import { Redirect, Stack, useLocalSearchParams } from "expo-router";
import { router } from "expo-router";
import { getSupabaseAuthClient } from "libs/supabase-auth";
import { useCallback, useEffect, useState } from "react";
import AccessDeniedImage from "../../../assets/undraw_access_denied.svg";

type CallbackStatus = "loading" | "success" | "error";

function parseHashParams(params: Record<string, string>) {
	const hash = params["#"];
	if (!hash) {
		return {};
	}

	return Object.fromEntries(new URLSearchParams(hash).entries());
}

const CallbackHeader = () => (
	<OnboardingAppBar showLogo onBackAction={() => router.back()} />
);

const CallbackRoute = () => {
	const params = useLocalSearchParams<{
		access_token?: string;
		refresh_token?: string;
	}>();
	const { access_token: accessToken, refresh_token: refreshToken } =
		parseHashParams(params);

	const provider = useAppStore((state) => state.app.auth?.provider);
	const isSupabaseProvider = provider === "supabase";

	const [status, setStatus] = useState<CallbackStatus>("loading");

	const setSession = useCallback(async () => {
		if (!(accessToken && refreshToken)) {
			setStatus("error");
			return;
		}

		try {
			const supabase = getSupabaseAuthClient();

			const { error } = await supabase.auth.setSession({
				access_token: accessToken,
				refresh_token: refreshToken,
			});

			if (error) {
				setStatus("error");
				return;
			}

			setStatus("success");
		} catch {
			setStatus("error");
		}
	}, [accessToken, refreshToken]);

	useEffect(() => {
		if (!isSupabaseProvider) {
			return;
		}
		setSession();
	}, [setSession, isSupabaseProvider]);

	if (!isSupabaseProvider) {
		return <Redirect href="/(auth)/login" />;
	}

	if (status === "loading") {
		return (
			<>
				<Stack.Screen
					options={{ headerShown: true, header: () => <CallbackHeader /> }}
				/>
				<Loader />
			</>
		);
	}

	if (status === "success") {
		return <Redirect href={"/home"} />;
	}

	return (
		<>
			<Stack.Screen
				options={{ headerShown: true, header: () => <CallbackHeader /> }}
			/>
			<EmptyState
				title="Unable to authenticate"
				subtitle="We are sorry, but we are unable to authenticate you. Go to login to continue."
				image={AccessDeniedImage}
				cta="Go to login"
				handleAction={() => {
					router.replace("/(auth)/login");
				}}
			/>
		</>
	);
};

export default CallbackRoute;
